import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden bg-forest text-cream"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-10 left-0 select-none font-serif text-[22vw] italic leading-none text-cream/[0.04]"
      >
        utsav
      </span>

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-24 pt-36 lg:grid-cols-12 lg:px-10">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-8 text-xs font-bold uppercase tracking-[0.3em] text-cream/50"
            data-testid="hero-eyebrow"
          >
            Dibrugarh · Assam · Est. 1999
          </motion.p>

          <h1 className="font-serif text-6xl font-medium leading-[0.95] tracking-tight sm:text-7xl lg:text-8xl">
            <MaskedLine delay={0.35}>Dibrugarh blooms</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="italic text-marigold">with us,</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>since 1999.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-cream/70"
            data-testid="hero-subtext"
          >
            The city's oldest flower house — fresh cut flowers, hand-tied bouquets,
            everlasting artificials and décor for weddings, birthdays and every
            celebration in between.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-shop-cta"
              onClick={() => navigate("/shop")}
              className="group flex items-center gap-3 rounded-full bg-cream px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-colors duration-300 hover:bg-marigold"
            >
              Shop the Collection
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-event-cta"
              onClick={() => scrollToId("#contact")}
              className="rounded-full border border-cream/30 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:border-cream hover:bg-cream hover:text-forest"
            >
              Plan an Event
            </button>
          </motion.div>
        </motion.div>

        <div className="relative hidden lg:col-span-5 lg:block">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
            className="relative overflow-hidden rounded-t-[999px]"
          >
            <motion.img
              src="/images/hero.jpg"
              alt="Signature floral arrangement by Utsav Flowers"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="h-[70vh] w-full scale-110 object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.4, ease: EASE }}
            className="absolute -left-10 bottom-10 flex h-28 w-28 items-center justify-center rounded-full border border-cream/20 bg-forest/80 backdrop-blur-md"
          >
            <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow p-2">
              <defs>
                <path id="circlePath" d="M 50,50 m -36,0 a 36,36 0 1,1 72,0 a 36,36 0 1,1 -72,0" />
              </defs>
              <text className="fill-cream/70" style={{ fontSize: "10.5px", letterSpacing: "2.5px" }}>
                <textPath href="#circlePath">FRESH EVERY DAWN · SINCE 1999 ·</textPath>
              </text>
            </svg>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("#story")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-cream/40 transition-colors hover:text-cream"
        aria-label="Scroll down"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
